import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Text,
  HStack,
  Button,
  IconButton,
} from 'src/components/ui';
import { CloseIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { FaMousePointer } from 'react-icons/fa';
import {
  TOP_BAR_HEIGHT,
  RIGHT_SIDEBAR_WIDTH_COLLAPSED,
} from 'src/components/campaigns/utils/constants';

// Tour step definitions for Step 2 (Designs Tour)
// Simplified to 3 steps:
// 1. Generate Designs button (outside modal)
// 2. Generate design button (inside Template Gallery modal)
// 3. Final explanation tooltip (fixed on right side, no overlay)
export const DESIGNS_TOUR_STEPS = {
  GENERATE_DESIGNS_BUTTON: 0,    // Highlight "Generate Designs" button
  TEMPLATE_GALLERY_SELECTED: 1,  // Point to "Generate design" button in modal
  TEMPLATE_GALLERY_GENERATE: 2,  // (skipped - step 1 covers the modal)
  FINAL_EXPLANATION: 3,          // Fixed tooltip explaining designs & creatives
  COMPLETE: 4,                   // Tour complete
};

interface DesignsTourProps {
  isActive: boolean;
  currentStep: number;
  onComplete: () => void;
  onSkip: () => void;
  onAdvance: () => void;
  setStep: (step: number) => void;
  onCloseTemplateGallery?: () => void;
}

interface TooltipPosition {
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
}

const DesignsTour: React.FC<DesignsTourProps> = ({
  isActive,
  currentStep,
  onComplete,
  onSkip,
  onAdvance,
  setStep,
  onCloseTemplateGallery,
}) => {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({});
  const [isVisible, setIsVisible] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [showClickEffect, setShowClickEffect] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const observerRef = useRef<MutationObserver | null>(null);
  const prevStepRef = useRef<number>(-1);
  const hasScrolledForStep0Ref = useRef<boolean>(false);

  // Check if current step is inside a modal (needs higher z-index)
  const isModalStep = currentStep === DESIGNS_TOUR_STEPS.TEMPLATE_GALLERY_SELECTED ||
                      currentStep === DESIGNS_TOUR_STEPS.TEMPLATE_GALLERY_GENERATE;

  // Check if current step is the final explanation (no overlay, user can interact)
  const isFinalStep = currentStep === DESIGNS_TOUR_STEPS.FINAL_EXPLANATION;

  // Check if step allows clicks to pass through overlay (for clicking the Generate Designs button)
  const allowClicksThrough = currentStep === DESIGNS_TOUR_STEPS.GENERATE_DESIGNS_BUTTON;

  // Check if this is the first step (needs tooltip above with upward arrow)
  const isFirstStep = currentStep === DESIGNS_TOUR_STEPS.GENERATE_DESIGNS_BUTTON;

  // Z-index values - higher for modal steps
  const baseZIndex = isModalStep ? 10150 : 9990;
  const overlayZIndex = baseZIndex;
  const borderZIndex = baseZIndex + 2;
  const badgeZIndex = baseZIndex + 8;
  const tooltipZIndex = baseZIndex + 8;
  const cursorZIndex = baseZIndex + 9;
  const headerOverlayZIndex = isModalStep ? 10250 : 10202;

  // Get target element based on current step
  const getTargetSelector = useCallback(() => {
    switch (currentStep) {
      case DESIGNS_TOUR_STEPS.GENERATE_DESIGNS_BUTTON:
        return '#campaign-generate-designs-btn, #campaign-config-form-submit-with-lock-modal';
      case DESIGNS_TOUR_STEPS.TEMPLATE_GALLERY_SELECTED:
        // Step 1: Point to the Generate design button in the modal (same as step 2)
        return '[data-tour="template-gallery-generate"]';
      case DESIGNS_TOUR_STEPS.TEMPLATE_GALLERY_GENERATE:
        return '[data-tour="template-gallery-generate"]';
      case DESIGNS_TOUR_STEPS.FINAL_EXPLANATION:
        // Step 3: Point to the Schedule & Publish button
        return '[data-tour="schedule-publish-button"]';
      default:
        return null;
    }
  }, [currentStep]);

  // Get step content
  const getStepContent = useCallback(() => {
    switch (currentStep) {
      case DESIGNS_TOUR_STEPS.GENERATE_DESIGNS_BUTTON:
        return {
          title: 'Time to Create Your Designs!',
          description: 'Click here to open the Template Gallery. We\'ve pre-selected 3 templates based on your campaign data. You can keep them, deselect, or choose different ones.',
          buttonText: 'Got it',
        };
      case DESIGNS_TOUR_STEPS.TEMPLATE_GALLERY_SELECTED:
        return {
          title: 'Generate Your Creatives',
          description: 'Once you\'re happy with your template selection, click here to create your ads.',
          buttonText: 'Got it',
        };
      case DESIGNS_TOUR_STEPS.TEMPLATE_GALLERY_GENERATE:
        // Skipped - step 1 covers the modal
        return { title: '', description: '', buttonText: '' };
      case DESIGNS_TOUR_STEPS.FINAL_EXPLANATION:
        return {
          title: 'Ready to Publish!',
          description: `Once your designs and creatives have finished uploading, you can edit the images and content of each channel. Once you're happy with everything, press this button to continue to the last step!`,
          buttonText: 'Finish Tour',
        };
      default:
        return { title: '', description: '', buttonText: '' };
    }
  }, [currentStep]);

  // Get display step number (3 total visible steps)
  const getDisplayStep = useCallback(() => {
    // Mapping: 0→1, 1→2, 3→3
    if (currentStep <= 1) {
      return currentStep + 1;
    }
    return 3; // FINAL_EXPLANATION is always step 3
  }, [currentStep]);

  const TOTAL_DISPLAY_STEPS = 3;

  // Scroll element into view with proper positioning
  const scrollElementIntoView = useCallback((element: Element) => {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Target position: element should be about 30% from top of viewport
    // This leaves room for the tooltip to appear below the highlighted element
    const targetY = viewportHeight * 0.30;
    const currentY = rect.top;
    const scrollNeeded = currentY - targetY;

    // Always scroll if the element is below 50% of viewport or above 20%
    // This ensures the element is well-positioned for viewing
    const shouldScroll = currentY > viewportHeight * 0.5 || currentY < viewportHeight * 0.2;

    if (shouldScroll && Math.abs(scrollNeeded) > 20) {
      window.scrollBy({
        top: scrollNeeded,
        behavior: 'smooth'
      });
    }
  }, []);

  // Update target element position
  const updateTargetPosition = useCallback(() => {
    const selector = getTargetSelector();
    if (!selector) {
      setTargetRect(null);
      return;
    }

    const element = document.querySelector(selector);
    if (element) {
      // Scroll element into view on first step (only once)
      if (currentStep === DESIGNS_TOUR_STEPS.GENERATE_DESIGNS_BUTTON && !hasScrolledForStep0Ref.current) {
        hasScrolledForStep0Ref.current = true;
        scrollElementIntoView(element);
      }

      const rect = element.getBoundingClientRect();
      setTargetRect(rect);
      setIsVisible(true);

      // Calculate tooltip position based on available space
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // For modal steps, position tooltip ABOVE the target (button is at bottom of modal)
      if (isModalStep) {
        setTooltipPosition({
          top: Math.max(100, rect.top - 180), // Closer to the button
          left: Math.max(20, Math.min(rect.left - 150, viewportWidth - 360)),
        });
      } else if (currentStep === DESIGNS_TOUR_STEPS.FINAL_EXPLANATION) {
        // For Schedule & Publish button, always position tooltip ABOVE the button
        // Leave space for arrow pointing down
        setTooltipPosition({
          top: Math.max(100, rect.top - 210),
          left: Math.max(20, Math.min(rect.left - 100, viewportWidth - 360)),
        });
      } else if (currentStep === DESIGNS_TOUR_STEPS.GENERATE_DESIGNS_BUTTON) {
        // For first step, position tooltip BELOW the button with space for upward arrow
        setTooltipPosition({
          top: rect.bottom + 45, // Space for arrow pointing up
          left: Math.max(20, Math.min(rect.left - 50, viewportWidth - 360)),
        });
      } else if (rect.bottom + 200 > viewportHeight) {
        // Position above if not enough space below
        setTooltipPosition({
          top: Math.max(100, rect.top - 180),
          left: Math.max(20, Math.min(rect.left, viewportWidth - 360)),
        });
      } else {
        // Position below the target - ensure tooltip stays on screen
        setTooltipPosition({
          top: rect.bottom + 16,
          left: Math.max(20, Math.min(rect.left, viewportWidth - 360)),
        });
      }

      // Update cursor position
      setCursorPosition({
        x: rect.left + rect.width / 2 - 12,
        y: rect.top + rect.height / 2 - 12,
      });
    } else {
      setTargetRect(null);
      setIsVisible(false);
    }
  }, [getTargetSelector, currentStep, isModalStep, scrollElementIntoView]);

  // Handle step transitions
  useEffect(() => {
    if (prevStepRef.current !== currentStep) {
      setIsTransitioning(true);
      setTimeout(() => {
        setIsTransitioning(false);
        prevStepRef.current = currentStep;
      }, 200);
    }
  }, [currentStep]);

  // Reset scroll tracking when tour becomes inactive
  useEffect(() => {
    if (!isActive) {
      hasScrolledForStep0Ref.current = false;
      prevStepRef.current = -1;
    }
  }, [isActive]);

  // Watch for DOM changes to find elements that might appear later
  useEffect(() => {
    if (!isActive) return;

    // Initial check with delay to allow DOM to settle
    // Use longer delay for first step and final step since they need time to render
    let delay = 300;
    if (currentStep === DESIGNS_TOUR_STEPS.GENERATE_DESIGNS_BUTTON) {
      delay = 600;
    } else if (currentStep === DESIGNS_TOUR_STEPS.FINAL_EXPLANATION) {
      delay = 500; // Give time for modal to fully close and button to be visible
    }
    const initialTimeout = setTimeout(() => {
      updateTargetPosition();
    }, delay);

    // Set up mutation observer to watch for element appearing
    observerRef.current = new MutationObserver(() => {
      updateTargetPosition();
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    // Also update on scroll/resize
    const handleUpdate = () => updateTargetPosition();
    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);

    // Periodic update to catch dynamic changes
    const interval = setInterval(updateTargetPosition, 500);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
      observerRef.current?.disconnect();
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
    };
  }, [isActive, currentStep, updateTargetPosition]);

  // Auto-advance from step 2 to step 3 when Template Gallery modal closes
  useEffect(() => {
    if (!isActive || currentStep !== DESIGNS_TOUR_STEPS.TEMPLATE_GALLERY_SELECTED) return;

    // Check periodically if the modal has closed (the generate button no longer exists)
    const checkModalClosed = setInterval(() => {
      const modalButton = document.querySelector('[data-tour="template-gallery-generate"]');
      const scheduleButton = document.querySelector('[data-tour="schedule-publish-button"]');

      // If modal button is gone AND schedule button is visible, advance to step 3
      if (!modalButton && scheduleButton) {
        clearInterval(checkModalClosed);
        setStep(DESIGNS_TOUR_STEPS.FINAL_EXPLANATION);
      }
    }, 300);

    return () => clearInterval(checkModalClosed);
  }, [isActive, currentStep, setStep]);

  // Show click effect periodically
  useEffect(() => {
    if (!isActive || !isVisible) return;

    const interval = setInterval(() => {
      setShowClickEffect(true);
      setTimeout(() => setShowClickEffect(false), 500);
    }, 3000);

    // Show immediately once
    setTimeout(() => {
      setShowClickEffect(true);
      setTimeout(() => setShowClickEffect(false), 500);
    }, 500);

    return () => clearInterval(interval);
  }, [isActive, isVisible, currentStep]);

  // Handle button click
  const handleButtonClick = () => {
    if (currentStep === DESIGNS_TOUR_STEPS.FINAL_EXPLANATION) {
      onComplete();
    } else if (currentStep === DESIGNS_TOUR_STEPS.TEMPLATE_GALLERY_SELECTED) {
      // Close the template gallery modal first
      if (onCloseTemplateGallery) {
        onCloseTemplateGallery();
      }
      // Skip step 2 (TEMPLATE_GALLERY_GENERATE) and go directly to step 3 (FINAL_EXPLANATION)
      // Small delay to allow modal to close before showing next step
      setTimeout(() => {
        setStep(DESIGNS_TOUR_STEPS.FINAL_EXPLANATION);
      }, 300);
    } else {
      onAdvance();
    }
  };

  // Handle skip/close for modal steps - only close the tour tooltip, NOT the modal
  const handleModalStepSkip = () => {
    // Just call onSkip which should only affect the tour, not the modal
    // The modal will stay open so user can continue working
    onSkip();
  };


  if (!isActive) {
    return null;
  }

  if (!isVisible || !targetRect) return null;

  const padding = 12;
  const rect = {
    left: targetRect.left - padding,
    top: targetRect.top - padding,
    right: targetRect.right + padding,
    bottom: targetRect.bottom + padding,
    width: targetRect.width + padding * 2,
    height: targetRect.height + padding * 2,
  };

  return createPortal(
    <Box>
      {/* Dark overlay with spotlight cutout - only for non-modal steps and not final step */}
      {/* NOTE: This overlay stays visible during transitions to avoid flash */}
      {/* Final step has no overlay so user can interact with the page */}
      {/* First step (GENERATE_DESIGNS_BUTTON) allows clicks through so user can click the button */}
      {/* Stops before right sidebar to avoid double overlay */}
      {!isModalStep && !isFinalStep && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
          bottom={0}
          zIndex={overlayZIndex}
          bg="blackAlpha.600"
          backdropFilter="blur(4px)"
          cursor={allowClicksThrough ? "default" : "not-allowed"}
          pointerEvents={allowClicksThrough ? "none" : "auto"}
          onClick={allowClicksThrough ? undefined : (e) => { e.preventDefault(); e.stopPropagation(); }}
          style={{
            clipPath: isTransitioning ? undefined : `polygon(
              0% 0%,
              0% 100%,
              ${rect.left}px 100%,
              ${rect.left}px ${rect.top}px,
              ${rect.right}px ${rect.top}px,
              ${rect.right}px ${rect.bottom}px,
              ${rect.left}px ${rect.bottom}px,
              ${rect.left}px 100%,
              100% 100%,
              100% 0%
            )`,
          }}
        />
      )}

      {/* Dark overlay for TopBar - same style as rest of overlay */}
      {!isModalStep && !isFinalStep && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
          h={TOP_BAR_HEIGHT}
          zIndex={headerOverlayZIndex}
          bg="blackAlpha.600"
          backdropFilter="blur(4px)"
          pointerEvents="none"
        />
      )}

      {/* Dark overlay for Right Sidebar - covers entire sidebar height */}
      {!isModalStep && !isFinalStep && (
        <Box
          position="fixed"
          top={0}
          right={0}
          bottom={0}
          w={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
          zIndex={headerOverlayZIndex}
          bg="blackAlpha.600"
          backdropFilter="blur(4px)"
          pointerEvents="none"
        />
      )}

      {/* Dark overlay for Right Sidebar - for modal steps NOT needed since modal has its own backdrop */}

      {/* Transitioning content - spotlight, tooltip, cursor */}
      <Box
        opacity={isTransitioning ? 0 : 1}
        transition="opacity 0.2s ease-in-out"
      >
      {/* Orange border around spotlight */}
      <Box
        position="fixed"
        left={`${rect.left}px`}
        top={`${rect.top}px`}
        width={`${rect.width}px`}
        height={`${rect.height}px`}
        borderRadius="8px"
        zIndex={borderZIndex}
        pointerEvents="none"
        border="3px solid"
        borderColor="orange.400"
        boxShadow="0 0 0 3px rgba(246, 133, 30, 0.25)"
      />

      {/* Step number badge - top left corner */}
      <Box
        position="fixed"
        left={`${rect.left - 12}px`}
        top={`${rect.top - 12}px`}
        zIndex={badgeZIndex}
        bg="orange.500"
        color="white"
        borderRadius="full"
        w="28px"
        h="28px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="sm"
        fontWeight="bold"
        boxShadow="md"
      >
        {getDisplayStep()}
      </Box>

      {/* Tooltip for step 1/3 (NOT modal, NOT final step) */}
      {!isFinalStep && !isModalStep && (
        <Box
          position="fixed"
          left={`${(tooltipPosition.left || 0 )- 30}px`}
          top={`${(tooltipPosition.top || 0 )- 20}px`}
          zIndex={tooltipZIndex}
          bg="white"
          borderRadius="lg"
          p={4}
          maxW="340px"
          pointerEvents="auto"
        >
          <HStack justify="space-between" align="flex-start" mb={2}>
            <Text fontWeight="bold" fontSize="md">
              {getStepContent().title}
            </Text>
            <IconButton
              aria-label="Close tour"
              icon={<CloseIcon boxSize={2.5} />}
              size="xs"
              variant="ghost"
              onClick={onSkip}
            />
          </HStack>
          <Text color="gray.600" fontSize="sm" mb={4}>
            {getStepContent().description}
          </Text>
          <HStack justify="space-between">
            <Button size="sm" variant="ghost" onClick={onSkip}>
              Skip
            </Button>
            <Text fontSize="xs" color="gray.500">
              Step {getDisplayStep()} of {TOTAL_DISPLAY_STEPS}
            </Text>
          </HStack>
        </Box>
      )}

      {/* Tooltip for step 2/3 (MODAL step) - separate to handle click propagation */}
      {isModalStep && (
        <Box
          position="fixed"
          left={`${(tooltipPosition.left || 0 )- 30}px`}
          top={`${(tooltipPosition.top || 0 )- 20}px`}
          zIndex={2147483647}
          bg="white"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
          p={4}
          maxW="340px"
          pointerEvents="auto"
          onClick={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          onPointerDown={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          style={{ isolation: 'isolate' }}
        >
          <HStack justify="space-between" align="flex-start" mb={2}>
            <Text fontWeight="bold" fontSize="md">
              {getStepContent().title}
            </Text>
            <IconButton
              aria-label="Close tour"
              icon={<CloseIcon boxSize={2.5} />}
              size="xs"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                handleModalStepSkip();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
              }}
            />
          </HStack>
          <Text color="gray.600" fontSize="sm" mb={4}>
            {getStepContent().description}
          </Text>
          <HStack justify="space-between">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                handleModalStepSkip();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
              }}
            >
              Skip
            </Button>
            <Text fontSize="xs" color="gray.500">
              Step {getDisplayStep()} of {TOTAL_DISPLAY_STEPS}
            </Text>
          </HStack>
        </Box>
      )}

      {/* Tooltip for step 3/3 (FINAL STEP) - SEPARATE COMPONENT for independent positioning */}
      {isFinalStep && (
        <Box
          position="fixed"
          left={`${(tooltipPosition.left || 0 )+10}px`}
          top={`${(tooltipPosition.top || 0 )- 45}px`}
          zIndex={tooltipZIndex}
          bg="white"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
          p={4}
          maxW="340px"
          pointerEvents="auto"
        >
          <HStack justify="space-between" align="flex-start" mb={2}>
            <Text fontWeight="bold" fontSize="md">
              {getStepContent().title}
            </Text>
            <IconButton
              aria-label="Close tour"
              icon={<CloseIcon boxSize={2.5} />}
              size="xs"
              variant="ghost"
              onClick={onSkip}
            />
          </HStack>
          <Text color="gray.600" fontSize="sm" mb={4}>
            {getStepContent().description}
          </Text>
          <HStack justify="space-between">
            <Button size="sm" variant="ghost" onClick={onSkip}>
              Skip
            </Button>
            <HStack spacing={3}>
              <Text fontSize="xs" color="gray.500">
                Step {getDisplayStep()} of {TOTAL_DISPLAY_STEPS}
              </Text>
            </HStack>
          </HStack>
        </Box>
      )}

      {/* Downward-pointing arrow for final step - connected to tooltip card */}
      {isFinalStep && tooltipPosition.top && targetRect && (
        <Box
          position="fixed"
          top={`${(tooltipPosition.top || 0) + 162}px`}
          left={`${targetRect.left + targetRect.width / 2 - 16}px`}
          zIndex={tooltipZIndex + 1}
          w={0}
          h={0}
          borderLeft="16px solid transparent"
          borderRight="16px solid transparent"
          borderTop="32px solid white"
          pointerEvents="none"

        />
      )}

      {/* Independent arrow for step 2/3 (TEMPLATE_GALLERY_SELECTED) - points from tooltip to Generate design button */}
      {currentStep === DESIGNS_TOUR_STEPS.TEMPLATE_GALLERY_SELECTED && tooltipPosition.top && targetRect && (
        <Box
          position="fixed"
          top={`${(tooltipPosition.top || 0) + 138}px`}
          left={`${(tooltipPosition.left || 0) + 205}px`}
          zIndex={2147483647}
          pointerEvents="none"
        >
          {/* Arrow pointing down to the Generate design button */}
          <Box
            w={0}
            h={0}
            borderLeft="16px solid transparent"
            borderRight="16px solid transparent"
            borderTop="30px solid white"
            filter="drop-shadow(0 2px 2px rgba(0,0,0,0.1))"
          />
        </Box>
      )}

      {/* Upward-pointing arrow for first step - from tooltip up to button */}
      {isFirstStep && tooltipPosition.top && targetRect && (
        <Box
          position="fixed"
          top={`${(tooltipPosition.top || 0) - 30}px`}
          left={`${targetRect.left + targetRect.width / 2 - 20}px`}
          zIndex={tooltipZIndex + 1}
          w={0}
          h={0}
          borderLeft="12px solid transparent"
          borderRight="12px solid transparent"
          borderBottom="28px solid white"
          pointerEvents="none"
        />
      )}

      {/* Animated cursor */}
      <Box
        position="fixed"
        left={`${cursorPosition.x}px`}
        top={`${cursorPosition.y}px`}
        zIndex={cursorZIndex}
        pointerEvents="none"
        transition="left 1.5s ease, top 1.5s ease"
      >
          <Box
            color="orange.500"
            filter="drop-shadow(0 2px 4px rgba(0,0,0,0.3))"
            transform={showClickEffect ? 'scale(0.85)' : 'scale(1)'}
            transition="transform 0.1s"
          >
            <FaMousePointer size={24} />
          </Box>
          {showClickEffect && (
            <Box
              position="absolute"
              top="50%"
              left="50%"
              w="30px"
              h="30px"
              borderRadius="50%"
              bg="orange.400"
              sx={{
                animation: 'ripple 0.5s ease-out',
                '@keyframes ripple': {
                  '0%': { transform: 'translate(-50%, -50%) scale(0)', opacity: 0.6 },
                  '100%': { transform: 'translate(-50%, -50%) scale(2)', opacity: 0 },
                },
              }}
            />
          )}
        </Box>
      </Box>
    </Box>,
    document.body
  );
};

export default DesignsTour;
